// src/pages/Main/themedShot.js

import React, { Component } from 'react';
import { Card } from 'react-bootstrap';
import { withTheme } from 'context/theme';

/**
 * A landing-page screenshot that follows the theme.
 *
 * `tools/shots/shots.json` publishes each captured surface twice — `<name>.png` in light and
 * `<name>.dark.png` in dark — so the page can stop showing a light photograph of the app to someone
 * reading a dark one. This resolves which of the pair to request; everything else about the image is
 * still the caller's.
 *
 * WHY NOT `<picture>` WITH A `media` SOURCE-SET. That is the obvious markup for this and it is wrong
 * here. `<source media="(prefers-color-scheme: dark)">` follows the OS, and the OS is not the answer:
 * a visitor who has explicitly picked Light on a dark-OS machine would get a dark screenshot on the
 * light page they asked for. That is precisely the bug `:not([data-theme="light"])` exists to prevent
 * in theme.css (see its three-group note), and a source-set would reintroduce it in markup, where no
 * CSS gate can see it. `Theme.resolved()` already collapses choice-plus-OS into the one answer in
 * force, so the component reads that instead.
 *
 * LIGHT IS THE FALLBACK, NOT A BRANCH. The test is `"dark" === theme`, so anything else — a theme
 * that could not be resolved, storage that throws, a browser with no matchMedia, `withTheme` absent
 * because someone rendered the inner class directly — lands on the light path, which is exactly the
 * markup that shipped before this component existed. The failure mode of the whole feature is
 * therefore "the page looks like it always did", never a broken image.
 *
 * IT STAYS A `Card.Img`. The dark-radius rule in index.css keys off Bootstrap's own `.card-img` and
 * `.card-img-top` — which of the two decides whether all four corners are rounded or only the top —
 * so `variant` has to keep reaching Bootstrap rather than being flattened into a plain `<img>`.
 */

const BASE = "/static/main/";

// The resolved-dark spelling, which is Theme.DARK. Written out rather than imported: this file needs
// nothing else from that module at runtime, and the string is half of shots.json's `dest` contract.
const DARK = "dark";

/**
 * The path rule, exported so it can be asserted directly rather than through a rendered tree.
 * `{theme}` expansion in shots.json's `dest` works the same way: light is the bare name.
 */
export function shotSrc(name, theme) {
  return `${BASE}${name}${DARK === theme ? ".dark" : ""}.png`;
}

/**
 * Marks the images that HAVE a dark half, which is what lets index.css take `--bh-media-dim` off them
 * without listing them. Emitted by the component rather than typed at the call sites: a shot cannot
 * then be themed and still be dimmed, because the same line does both.
 */
export const THEMED_CLASS = "main-shot-themed";

export class ThemedShot extends Component {
  render() {
    // `theme` and `themeChoice` are injected by withTheme and are not DOM attributes; spreading them
    // onto Card.Img would reach <img> and warn. `themeChoice` is unused here on purpose — this
    // component wants the answer in force, not how it was arrived at.
    const { name, theme, themeChoice, className, ...rest } = this.props;
    const classes = [className, THEMED_CLASS].filter(Boolean).join(" ");
    return <Card.Img {...rest} className={classes} src={shotSrc(name, theme)} />;
  }
}

export default withTheme(ThemedShot);
